import { useMutation, useQueryClient } from "@tanstack/react-query";
import estimateProvider from "@/providers/estimate.provider";
import type { CreateOrUpdateItemLinePayload, CreateOrUpdateItemLineResponse, LineItemData } from "@/interfaces/payloads/line-item";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";
import type { GetEstimateDetailData } from "@/hooks/queries/estimates/useGetEstimateDetailQuery";
import { applyOptimisticUpdate } from "@/lib/estimate-line-items";

export interface UpdateLineItemMutationParams {
  estimateId: string;
  pageId: string;
  lineItemData: LineItemData[];
  tupleId?: string;
  source?: string;
  metadata?: Record<string, unknown>;
}

export function useUpdateLineItemMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateLineItemMutationParams): Promise<CreateOrUpdateItemLineResponse> => {
      const payload: CreateOrUpdateItemLinePayload = {
        lineItemId: params.tupleId || params.lineItemData[0]?.tupleId || "",
        estimateId: params.estimateId,
        pageId: params.pageId,
        lineItemData: params.lineItemData,
        source: params.source ?? "",
        metadata: params.metadata ?? {},
      };

      const response = await estimateProvider.updateOrCreateLineItem(payload);

      if (!response.success) {
        throw new Error("Failed to update line item");
      }

      return response;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: ESTIMATE_KEYS.detail(variables.estimateId),
      });

      const previousData = queryClient.getQueryData<GetEstimateDetailData>(ESTIMATE_KEYS.detail(variables.estimateId));

      if (previousData) {
        const optimisticData = applyOptimisticUpdate(previousData, variables);
        if (optimisticData) {
          queryClient.setQueryData(ESTIMATE_KEYS.detail(variables.estimateId), optimisticData);
        }
      }

      return { previousData };
    },
    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(ESTIMATE_KEYS.detail(variables.estimateId), context.previousData);
      }
      console.error("Update line item error:", error);
    },
    onSuccess: ({ data }, variables) => {
      queryClient.setQueryData<GetEstimateDetailData>(ESTIMATE_KEYS.detail(variables.estimateId), (old) => {
        if (old == null) return old; // no cached detail — leave cache unchanged
        return { ...old, estimationDetail: data };
      });
    },
  });
}
